/**
 * useAdminDashboardStats Hook
 *
 * Fetches and transforms global dashboard statistics for admin view
 */

import { useMemo } from 'react';

import { useGetAdminGlobalStatsQuery } from '../../store/api';
import type { GlobalDashboardStats, QualityDistributionData } from '../../types';

interface UseAdminDashboardStatsResult {
  /** Dashboard statistics */
  stats: GlobalDashboardStats | undefined;
  /** Quality distribution data */
  qualityDistribution: QualityDistributionData | undefined;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
  /** Computed metrics */
  computed: {
    /** Clean rate as percentage */
    cleanRate: number | null;
  };
}

export const useAdminDashboardStats = (): UseAdminDashboardStatsResult => {
  const { data, isLoading, error, refetch: refetchQuery } = useGetAdminGlobalStatsQuery();

  const refetch = (): void => {
    void refetchQuery();
  };

  const computed = useMemo(() => {
    if (!data) {
      return {
        cleanRate: null,
      };
    }

    const totalNotes = data.total_notes_audited || 0;
    const notesClean = data.notes_clean || 0;
    const cleanRate = totalNotes > 0 ? (notesClean / totalNotes) * 100 : null;

    return {
      cleanRate,
    };
  }, [data]);

  return {
    stats: data,
    qualityDistribution: data?.quality_distribution,
    isLoading,
    error: error ? 'Failed to load dashboard statistics' : null,
    refetch,
    computed,
  };
};

export default useAdminDashboardStats;

